import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { CommonModule } from '@angular/common';
import { Component, ElementRef, ViewChild, computed, inject, input, output } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import {
  MatAutocompleteModule,
  MatAutocompleteSelectedEvent,
} from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatChipInputEvent, MatChipsModule } from '@angular/material/chips';
import { MatOptionModule } from '@angular/material/core';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatTooltipModule } from '@angular/material/tooltip';
import { webDtos } from '@vectronic/openApi-admin';
import { ConfirmationDialogComponent } from '@vectronic/ui';
import { DialogService, TableCollapseService } from '@vectronic/util';

@Component({
  selector: 'table-movebank-forwarding-chips-cell',
  templateUrl: './movebank-forwarding-chips-cell.component.html',
  styleUrls: ['./movebank-forwarding-chips-cell.component.scss'],
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatInputModule,
    MatTooltipModule,
    MatButtonModule,
    MatIconModule,
    MatChipsModule,
    MatOptionModule,
    MatAutocompleteModule,
  ],
})
export class MovebankForwardingChipsCellComponent {
  private dialogService = inject(DialogService);
  private tableCollapseService = inject(TableCollapseService);

  collarId = input<number>();
  selectedMovebankForwarders = input<webDtos.DeviceMovebankInformation[]>();
  movebankForwarders = input<webDtos.DeviceMovebankInformation[]>();
  readonly = input<boolean>();
  isCompactModeActivated = input<boolean>();

  addMovebankForwarder = output<{
    idDevice: number;
    movebankInformation: webDtos.DeviceMovebankInformation;
  }>();
  deleteMovebankForwarder = output<{
    idDevice: number;
    movebankInformation: webDtos.DeviceMovebankInformation;
  }>();

  separatorKeysCodes: number[] = [ENTER, COMMA];
  formControl = new FormControl('');
  inputSignal = toSignal(this.formControl.valueChanges, { initialValue: null });
  filtered = computed(() =>
    this.inputSignal()
      ? this._filter(this.inputSignal(), this.movebankForwarders())
      : this.movebankForwarders()?.slice(),
  );

  @ViewChild('input') input: ElementRef<HTMLInputElement>;

  add(event: MatChipInputEvent) {
    const value = (event.value || '')?.trim();
    this.addMovebankForwarder.emit({
      idDevice: this.collarId(),
      movebankInformation: { movebankUsername: undefined, inventaUsername: value },
    });
    event.chipInput?.clear();
    this.formControl.setValue(null);
    if (this.input?.nativeElement.value !== undefined) {
      this.input.nativeElement.value = '';
    }
  }

  addButton() {
    this.add({
      input: this.input?.nativeElement,
      value: this.formControl.value,
      chipInput: undefined,
    });
  }

  onRemoveRule(movebankInformation: webDtos.DeviceMovebankInformation) {
    this.dialogService.open(ConfirmationDialogComponent, {
      afterConfirm: () =>
        this.deleteMovebankForwarder.emit({
          idDevice: this.collarId(),
          movebankInformation,
        }),
    });
  }

  selected(event: MatAutocompleteSelectedEvent) {
    const movebankForwarder = this.movebankForwarders()?.find(
      ({ movebankUsername }) => movebankUsername === event.option.value,
    );
    if (movebankForwarder) {
      this.addMovebankForwarder.emit({
        idDevice: this.collarId(),
        movebankInformation: movebankForwarder,
      });
    }
    this.formControl.setValue(null);
    this.input.nativeElement.value = '';
  }

  private _filter(
    value: string,
    data: webDtos.DeviceMovebankInformation[],
  ): webDtos.DeviceMovebankInformation[] {
    if (typeof value !== 'string') return data.slice();
    const filterValue = value.toLowerCase();
    return data?.filter((address) => address.movebankUsername.toLowerCase().includes(filterValue));
  }

  onClickShowMore() {
    if (!this.showMore) {
      this.tableCollapseService.setMovebankForwarerSelectedCollarId(this.collarId());
      setTimeout(() => {
        this.input?.nativeElement?.focus();
      }, 400);
    }
  }

  onClickShowLess(event: MouseEvent) {
    event.stopPropagation();
    if (this.showMore) {
      this.tableCollapseService.setMovebankForwarerSelectedCollarId(undefined);
    }
  }

  get showMore() {
    return this.tableCollapseService.isMovebankForwarderSelectedCollarId(this.collarId());
  }
}
